import { useContext, useEffect, useMemo, useState } from "react";
import { useTable, useSortBy, usePagination, Column } from "react-table";
import TableCSVExport from "../../../../component/TableCSVExport";
import ObjectTuningApi from "../../../../api/object-tuning/ObjectTuningApi";
import { TimeRangeContext } from "../../../../context/TimeRangeContext";

// Components
import TablePagination from "../../../../component/TablePagination";
import ObjectDetailsPanel from "./ObjectDetailsPanel";
import NoData from "../../../../component/NoData";

// Types
import TableStatisticsType from "../../../../types/ObjectBrowser/TableStatisticsType";
import IndexStatisticsType from "../../../../types/ObjectBrowser/IndexStatisticsType";

import './style.scss';
import InstanceTarget from "../../../../types/instance/InstanceTarget";
import { useNavigate, useLocation } from "react-router-dom";
import TabDisabledBanner from '../../../../component/instance/TabDisabledBanner';
import { getDefaultPageSize } from "../../../../helpers/utils";

const ObjectBrowser = (props: { instance: InstanceTarget, instanceTypes: any[] }) => {
    const [typeFilter, setTypeFilter] = useState("Table");
    const [dbFilter, setDbFilter] = useState("All Databases");
    const [schemaFilter, setSchemaFilter] = useState("All Schemas");
    const [globalFilter, setGlobalFilter] = useState("");
    const timeRangeContext = useContext(TimeRangeContext)
    const [loading, setLoading] = useState(true);
    const { instance } = props
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    const [tableStatistics, setTableStatistics] = useState<TableStatisticsType[]>([]);
    const [indexStatistics, setIndexStatistics] = useState<IndexStatisticsType[]>([]);

    const getUniqueValues = (data: any[], key: string): any[] => {
        return Array.from(new Set(data.map(item => item[key])));
    };

    const objectTypes = ["Table", "Index"];

    const getFilteredDatabases = (data: any[]) => {
        if (schemaFilter === "All Schemas") {
            return getUniqueValues(data, typeFilter === "Table" ? "database" : "database");
        }
        return getUniqueValues(
            data.filter(item => 
                typeFilter === "Table" 
                    ? item.tableschema === schemaFilter 
                    : item.indexschema === schemaFilter
            ),
            "database"
        );
    };

    const getFilteredSchemas = (data: any[]) => {
        if (dbFilter === "All Databases") {
            return getUniqueValues(data, typeFilter === "Table" ? "tableschema" : "indexschema");
        }
        return getUniqueValues(
            data.filter(item => item.database === dbFilter),
            typeFilter === "Table" ? "tableschema" : "indexschema"
        );
    };

    const databaseNames = useMemo(() => {
        const data = typeFilter === "Table" ? tableStatistics : indexStatistics;
        return getFilteredDatabases(data);
    }, [typeFilter, tableStatistics, indexStatistics, schemaFilter]);

    const schemaNames = useMemo(() => {
        const data = typeFilter === "Table" ? tableStatistics : indexStatistics;
        return getFilteredSchemas(data);
    }, [typeFilter, tableStatistics, indexStatistics, dbFilter]);

    useEffect(() => {
        // Reset filters when switching between Table and Index views
        if (
            (dbFilter !== "All Databases" && !databaseNames.includes(dbFilter)) ||
            (schemaFilter !== "All Schemas" && !schemaNames.includes(schemaFilter))
        ) {
            resetFilters();
        }
    }, [databaseNames, schemaNames]);

    useEffect(() => {
        const getTableStatistic = async () => {
            const tableStatisticsData = await ObjectTuningApi.getTableStatistic(timeRangeContext, instance.id)
            setLoading(false)
            setTableStatistics(tableStatisticsData)
        }

        const getIndexStatistic = async () => {
            const indexStatisticData = await ObjectTuningApi.getIndexStatistic(timeRangeContext, instance.id)
            setLoading(false)
            setIndexStatistics(indexStatisticData)
        }


        void getTableStatistic()
        void getIndexStatistic()

    }, [timeRangeContext]);

    // State for details panel
    const [selectedObject, setSelectedObject] = useState<any>(null);
    const [panelOpen, setPanelOpen] = useState(false);

    const openPanel = (object: any, type: string) => {
        setSelectedObject({
            ...object,
            instanceId: instance.id,
            objectType: type,
            tablename: object.tablename,
            indexname: object.indexname
        });
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setSelectedObject(null);
    };

    const resetFilters = () => {
        setDbFilter("All Databases");
        setSchemaFilter("All Schemas");
        setGlobalFilter("");
    };

    const filteredData = useMemo(() => {
        const applyFilters = (row: any) => {
            const searchTerm = globalFilter.toLowerCase();

            // Search across all columns
            const matchesSearch = searchTerm === "" || [
                row.objectType,
                typeFilter === "Table" ? row.tablename : row.indexname,
                row.database,
                row.tableschema,
                row.tablename,
                row.rows,
                // Convert numeric values to string for searching
                row.indexsizebytes != null ? (row.indexsizebytes / 1024 / 1024).toFixed(2) : "",
                row.tablesizebytes != null ? (row.tablesizebytes / 1024 / 1024).toFixed(2) : "",
                row.totalsizebytes != null ? (row.totalsizebytes / 1024 / 1024).toFixed(2) : ""
            ].some(value =>
                    value != null && String(value).toLowerCase().includes(searchTerm)
            );

            return (
                    (dbFilter === "All Databases" || row.database === dbFilter) &&
                    (schemaFilter === "All Schemas" || row.tableschema === schemaFilter) &&
                    matchesSearch
            );
        };

        return typeFilter === "Table"
                ? tableStatistics.filter(applyFilters)
                : indexStatistics.filter(applyFilters);
    }, [typeFilter, dbFilter, schemaFilter, globalFilter, tableStatistics, indexStatistics]);

    const columns: Column[] = useMemo(() => [
        ...(typeFilter === "Table" ? [
            {
                Header: "Table",
                accessor: "tablename",
                headerClassName: "text-nowrap",
                Cell: ({ row, value }: { row: any, value: string }) => (
                        <span className='text-nowrap'>
                        <i className="fa fa-table me-1" aria-hidden="true"></i>
                           <span className='panel-link' onClick={() => openPanel(row.original, 'Table')}> {value}</span>
                    </span>
                ),
            },
        ] : []),
        ...(typeFilter === "Index" ? [
            {
                Header: "Index",
                accessor: "indexname",
                headerClassName: "text-nowrap",
                Cell: ({ row, value }: { row: any, value: string }) => (
                        <span className='text-nowrap'>
                             <i className="fa fa-list-ol me-1" aria-hidden="true"></i>
                           <span className='panel-link' onClick={() => openPanel(row.original, 'Index')}> {value}</span>
                    </span>
                ),
            },
        ] : []),
        {
            Header: typeFilter === "Table" ? "Schema" : "Index Schema",
            accessor: typeFilter === "Table" ? "tableschema" : "indexschema",
            headerClassName: "text-nowrap",
            className: "text-nowrap",
        },
        {
            Header: "Database",
            accessor: "database",
            headerClassName: "text-nowrap",
        },
        ...(typeFilter === "Table" ? [
            {
                Header: "Table Size (MB)",
                accessor: "tablesizebytes",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
                Cell: ({ value }: { value: number | null }) => (value != null ? (value / 1024 / 1024).toFixed(2) : "-"),
            },
            {
                Header: "Index Size (MB)",
                accessor: "indexsizebytes",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
                Cell: ({ value }: { value: number | null }) => (value != null ? (value / 1024 / 1024).toFixed(2) : "-"),
            },
            {
                Header: "Total Size (MB)",
                accessor: "totalsizebytes",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
                Cell: ({ value }: { value: number | null }) => (value != null ? (value / 1024 / 1024).toFixed(2) : "-"),
            },
            {
                Header: "Rows",
                accessor: "rows",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
            }
        ] : []),
        ...(typeFilter === "Index" ? [
            {
                Header: "Index Size (MB)",
                accessor: "indexsizebytes",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
                Cell: ({ value }: { value: number | null }) => (value != null ? (value / 1024 / 1024).toFixed(2) : "-"),
            },
            {
                Header: "Table Size (MB)",
                accessor: "tablesizebytes",
                className: "text-end",
                headerClassName: "text-nowrap text-end",
                Cell: ({ value }: { value: number | null }) => (value != null ? (value / 1024 / 1024).toFixed(2) : "-"),
            },
            {
                Header: "Table",
                accessor: "tablename",
                headerClassName: "text-nowrap",
                Cell: ({ row, value }: { row: any, value: string }) => (
                        <span>
                           <span className='panel-link' onClick={() => openPanel(row.original, 'Table')}> {value}</span>
                    </span>
                ),
            },
            {
                Header: "Table Schema",
                accessor: "tableschema",
                headerClassName: "text-nowrap",
            },
        ] : [])
    ], [typeFilter]);

    const tableInstance = useTable(
            {
                columns,
                data: filteredData,
                initialState: { pageIndex: initialPageIndex, pageSize: initialPageSize, sortBy: [typeFilter === 'Table' ? { id: 'totalsizebytes', desc: true } : { id: 'indexsizebytes', desc: true }] },
            },
            useSortBy,
            usePagination
    );

    const transformCsvData = useMemo(() => (csvData: any[]) => {
        return csvData.map((row: any) => {
            const out = { ...row };
            ['tablesizebytes', 'indexsizebytes', 'totalsizebytes'].forEach(key => {
                if (key in out && out[key] != null) {
                    out[key] = (Number(out[key]) / 1024 / 1024).toFixed(2);
                } else if (key in out) {
                    out[key] = '-';
                }
            });
            return out;
        });
    }, []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        
        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [pageIndex, pageSize, navigate, location.search]);

    const changeType = (value: string) => {
        resetFilters()
        closePanel()
        setTypeFilter(value)
    }

    const getRowClassName = (selectedItem: any) => {
        const className = "table-row";
        const isSameObject = selectedObject &&
                ['tablename', 'tableschema', 'database', 'indexschema', 'indexname'].every(
                        key => selectedItem[key] === selectedObject[key]
                );

        return isSameObject ? `${className} selected` : className;
    };

    return (
            <>
                <TabDisabledBanner instance={instance} instanceTypes={props.instanceTypes} collectFlag={!!instance.collectobjectstatistics} defaultCollectKey="defaultcollectobjectstatistics" featureName="Object Statistics" />
                <div className="row row-cols-1 object-browser">
                    <div className="col">
                        <div className="card">
                            <div className="card-header">
                                <i className="fal fa-table fa-fw" aria-hidden="true"/> Object Browser
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseContent" aria-expanded="false" aria-controls="collapseContent"></i>
                            </div>
                            <div id="collapseContent" className="card-body">
                                <div className="row row-cols-1 row-cols-md-2 table-search">
                                    <div className="col col-md-8 text-start">
                                        <label className='mx-2'>
                                            <select
                                                    className="form-select"
                                                    value={typeFilter}
                                                    onChange={e => changeType(e.target.value)}
                                            >
                                                {objectTypes.map(type => (
                                                        <option key={type} value={type}>
                                                            {type === "Table" ? "Tables" : "Indexes"}
                                                        </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className='mx-2'>
                                            <select
                                                    className="form-select mx-1"
                                                    value={schemaFilter}
                                                    onChange={e => setSchemaFilter(e.target.value)}
                                            >
                                                <option>All Schemas</option>
                                                {schemaNames.map(schema => <option key={schema} value={schema}>{schema}</option>)}
                                            </select>
                                        </label>
                                        <label className='mx-2'>
                                            <select
                                                    className="form-select mx-1"
                                                    value={dbFilter}
                                                    onChange={e => setDbFilter(e.target.value)}
                                            >
                                                <option>All Databases</option>
                                                {databaseNames.map(db => <option key={db} value={db}>{db}</option>)}
                                            </select>
                                        </label>
                                    </div>
                                    <div className="col col-md-2 text-end">

                                        <TableCSVExport
                                            tableInstance={tableInstance}
                                            filename="Object Statistics "
                                            transformData={transformCsvData}
                                        />

                                        <button className="btn btn-sm btn-dark" onClick={resetFilters}><i
                                                className="far fa-undo"/><span>Clear</span></button>

                                    </div>

                                    <div className="col col-md-2">
                                        <input type="text" autoFocus className="form-control form-control-sm" placeholder="Search across all columns"
                                               value={globalFilter} data-lpignore={true}
                                               onChange={(e) => setGlobalFilter(e.target.value)}/>
                                    </div>
                                </div>

                                <div style={{ overflowX: "auto" }}>
                                    <table
                                            {...getTableProps()}
                                            className="object-browser-table table"
                                            style={{ minWidth: 900 }}
                                    >
                                        <thead>
                                        {headerGroups.map(headerGroup => (
                                                <tr {...headerGroup.getHeaderGroupProps()}>
                                                    {headerGroup.headers.map(column => (
                                                            <th
                                                                    {...column.getHeaderProps((column as any).getSortByToggleProps())}
                                                                    className={(column as any).headerClassName}
                                                            >
                                                                {column.render("Header")}

                                                                {column.canSort ? (column.isSorted ? (column.isSortedDesc ?
                                                                                <i className="fal fa-sort-amount-up-alt"></i> :
                                                                                <i className="fal fa-sort-amount-down-alt"></i>) :
                                                                        <i className="fal fa-sort-amount-down-alt text-light"></i>) : ''}
                                                            </th>
                                                    ))}
                                                </tr>
                                        ))}
                                        </thead>
                                        <tbody {...getTableBodyProps()}>
                                        {page.map(row => {
                                            prepareRow(row);
                                            return (
                                                    <tr {...row.getRowProps()} className={getRowClassName(row.original)}>
                                                        {row.cells.map(cell => (
                                                                <td className={(cell.column as any).className} {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                                        ))}
                                                    </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                    <NoData error={null} loading={loading} length={filteredData.length}/>
                                    <TablePagination
                                            length={filteredData.length}
                                            pageSize={pageSize}
                                            setPageSize={setPageSize}
                                            pageOptions={pageOptions}
                                            pageCount={pageCount}
                                            canPreviousPage={canPreviousPage}
                                            previousPage={previousPage}
                                            canNextPage={canNextPage}
                                            nextPage={nextPage}
                                            gotoPage={gotoPage}
                                            pageIndex={pageIndex}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Slide-out panel for object details */}
                <ObjectDetailsPanel
                        object={selectedObject}
                        open={panelOpen}
                        onClose={closePanel}
                />
            </>
    );
};

export default ObjectBrowser;
